import { cn } from "@gigpro/ui/tailwind/cn";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import type * as React from "react";
import { Ellipsis } from "../../Icons";
import { Button, type ButtonProps } from "../Button";
import { Checkbox } from "../Checkbox";
import { usePortalScope } from "../PortalScope";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuContent = ({
  ref,
  className,
  children,
  side = "right",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) => {
  const scope = usePortalScope();
  return (
    <DropdownMenuPrimitive.Content
      ref={ref}
      side={side}
      sideOffset={4}
      collisionPadding={10}
      className={cn(
        "z-50 max-h-(--radix-dropdown-menu-content-available-height) max-w-(--radix-dropdown-menu-content-available-width) min-w-[8rem] animate-in overflow-auto rounded-sm bg-white p-1 font-semibold text-brand shadow-back data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...scope}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Content>
  );
};
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuLabel = ({
  ref,
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & { inset?: boolean }) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-normal font-semibold text-gray", inset && "pl-8", className)}
    {...props}
  />
);
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuItem = ({
  ref,
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & { inset?: boolean }) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer items-center gap-1.5 rounded-sm px-2 py-1.5 text-normal transition-colors outline-none select-none focus:bg-background data-disabled:pointer-events-none data-disabled:opacity-50 [&>svg]:size-3.5 [&>svg]:shrink-0",
      inset && "pl-8",
      className,
    )}
    {...props}
  />
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) => (
  <DropdownMenuPrimitive.Separator ref={ref} className={cn("-mx-1 my-1 h-px bg-light-gray", className)} {...props} />
);
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span className={cn("ml-auto text-extraSmall tracking-widest opacity-60", className)} {...props} />
);
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuRadioItem = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      "relative flex cursor-default items-center rounded-sm px-3 py-1.5 text-large font-regular text-black transition-colors outline-none select-none hover:bg-light-gray data-disabled:pointer-events-none data-disabled:opacity-50 data-[state=checked]:font-semibold",
      className,
    )}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.RadioItem>
);
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuCheckboxItem = ({
  ref,
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      "relative flex cursor-default items-center rounded-sm py-1.5 pr-3 pl-8 text-large font-regular text-black transition-colors outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    checked={checked}
    {...props}
  >
    <Checkbox checked={checked} className="absolute left-2 size-3.5" />
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
);
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

export interface DropdownMenuButtonProps
  extends
    Omit<React.ComponentProps<typeof DropdownMenuTrigger>, "asChild">,
    Pick<React.ComponentProps<typeof DropdownMenu>, "open" | "onOpenChange">,
    Pick<React.ComponentProps<typeof DropdownMenuPrimitive.Content>, "side"> {
  contentClassName?: string;
  icon?: React.ReactNode;
  variant?: Exclude<ButtonProps["variant"], undefined>;
}

const DropdownMenuButton = ({
  ref,
  children,
  contentClassName,
  side,
  open,
  onOpenChange,
  icon,
  variant,
  ...props
}: DropdownMenuButtonProps) => (
  <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
    <DropdownMenuTrigger ref={ref} asChild {...props}>
      <Button icon variant={variant}>
        {icon === undefined ? <Ellipsis /> : icon}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent side={side} className={contentClassName}>
        {children}
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenu>
);
DropdownMenuButton.displayName = "DropdownMenuButton";

export {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  DropdownMenuLabel,
};
